// ============================================================
// IAP SERVICE - Apple In-App Purchase via cordova-plugin-purchase
// Only active on native iOS. Web and Android use Stripe.
// ============================================================

import { Capacitor } from '@capacitor/core';
import { getAuth } from 'firebase/auth';
import { API_BASE } from '../utils/apiBase';

// Product IDs matching App Store Connect consumable products
export const IAP_PRODUCTS = {
  PLAYCOINS_10: 'com.pokersweepstars.playcoins.10',
  PLAYCOINS_25: 'com.pokersweepstars.playcoins.25',
  PLAYCOINS_50: 'com.pokersweepstars.playcoins.50',
  PLAYCOINS_100: 'com.pokersweepstars.playcoins.100',
} as const;

// Map USD amounts to product IDs
const AMOUNT_TO_PRODUCT: Record<number, string> = {
  10: IAP_PRODUCTS.PLAYCOINS_10,
  25: IAP_PRODUCTS.PLAYCOINS_25,
  50: IAP_PRODUCTS.PLAYCOINS_50,
  100: IAP_PRODUCTS.PLAYCOINS_100,
};

// Backend API for receipt validation
const API_URL = API_BASE;

/**
 * Check if IAP should be used (native iOS only)
 */
export const shouldUseIAP = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
};

/**
 * Get the CdvPurchase store instance (lazy loaded)
 */
const getStore = (): any => {
  const win = window as any;
  if (!win.CdvPurchase?.store) {
    throw new Error('IAP store not available. Ensure cordova-plugin-purchase is installed.');
  }
  return win.CdvPurchase.store;
};

/**
 * Initialize the IAP store and register products.
 * Call this once when the app starts on iOS.
 */
export const initializeIAP = async (): Promise<void> => {
  if (!shouldUseIAP()) return;

  try {
    const store = getStore();
    const { ProductType, Platform } = window.CdvPurchase || {};

    // Register all consumable products
    Object.values(IAP_PRODUCTS).forEach(productId => {
      store.register({
        id: productId,
        type: ProductType?.CONSUMABLE || 'consumable',
        platform: Platform?.APPLE_APPSTORE || 'ios-appstore',
      });
    });

    // Set up receipt validation via our backend
    store.validator = async (receipt: any, callback: any) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          callback(false, { error: { code: 0, message: 'User not authenticated' } });
          return;
        }

        const token = await user.getIdToken();
        const response = await fetch(`${API_URL}/validateIAPReceipt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            receipt: receipt.transaction?.appStoreReceipt || receipt.transaction?.receipt,
            productId: receipt.id,
            transactionId: receipt.transaction?.id,
          }),
        });

        const result = await response.json();
        if (response.ok && result.ok) {
          callback(true, result);
        } else {
          callback(false, { error: { code: result.code || 0, message: result.error || 'Validation failed' } });
        }
      } catch (error: any) {
        console.error('IAP validation error:', error);
        callback(false, { error: { code: 0, message: error.message || 'Network error' } });
      }
    };

    // v13 does nothing on its own after a purchase is approved: the app has
    // to ask for verification, and once verified it has to finish (consume)
    // the transaction. Without this chain the validator above is never
    // called and StoreKit re-delivers the unfinished purchase every launch.
    store.when()
      .approved((transaction: any) => transaction.verify())
      .verified((receipt: any) => receipt.finish())
      .unverified((unverified: any) => {
        console.warn('[IAP] Receipt could not be verified:', unverified?.payload?.error?.message || unverified);
      });

    // Initialize the store
    await store.initialize();
    console.log('[IAP] Store initialized successfully');
  } catch (error) {
    console.error('[IAP] Failed to initialize store:', error);
  }
};

/**
 * Purchase PlayCoins via Apple IAP.
 * Returns a promise that resolves when the purchase is complete.
 */
export const purchasePlayCoins = async (usdAmount: number): Promise<{ success: boolean; error?: string }> => {
  if (!shouldUseIAP()) {
    return { success: false, error: 'IAP not available on this platform' };
  }

  const productId = AMOUNT_TO_PRODUCT[usdAmount];
  if (!productId) {
    return { success: false, error: `No IAP product for $${usdAmount}. Use preset amounts: $10, $25, $50, $100.` };
  }

  try {
    const store = getStore();

    // Get the product offer
    const product = store.get(productId);
    if (!product) {
      return { success: false, error: 'Product not found in store. Please try again.' };
    }

    const offer = product.getOffer();
    if (!offer) {
      return { success: false, error: 'No offer available for this product.' };
    }

    // Initiate the purchase
    const result = await store.order(offer);
    if (result && result.isError) {
      return { success: false, error: result.message || 'Purchase failed' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('[IAP] Purchase error:', error);
    if (error.code === 'E_USER_CANCELLED' || error.message?.includes('cancelled')) {
      return { success: false, error: 'Purchase cancelled' };
    }
    return { success: false, error: error.message || 'Purchase failed' };
  }
};

/**
 * Restore previous purchases (required by App Store guidelines)
 */
export const restorePurchases = async (): Promise<void> => {
  if (!shouldUseIAP()) return;

  try {
    const store = getStore();
    await store.restorePurchases();
    console.log('[IAP] Purchases restored');
  } catch (error) {
    console.error('[IAP] Restore error:', error);
  }
};

/**
 * Get available IAP products with prices
 */
export const getProducts = (): any[] => {
  if (!shouldUseIAP()) return [];

  try {
    const store = getStore();
    return Object.values(IAP_PRODUCTS).map(id => store.get(id)).filter(Boolean);
  } catch {
    return [];
  }
};

// Declare CdvPurchase on window for TypeScript
declare global {
  interface Window {
    CdvPurchase?: any;
  }
}

export default {
  shouldUseIAP,
  initializeIAP,
  purchasePlayCoins,
  restorePurchases,
  getProducts,
  IAP_PRODUCTS,
};
